import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Form, Modal, Select, Skeleton, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { ContactListItem, DealItem, ObjectType, OrganizationListItem } from '@crm/shared';
import { FieldFormItem, fromFormValues } from './DynamicField';
import { useFields } from '../features/fields/api';
import { quickAddFields } from '../features/fields/system-keys';
import { useCreateContact } from '../features/contacts/api';
import { useCreateOrganization } from '../features/organizations/api';
import { useCreateDeal, usePipelines } from '../features/deals/api';
import { errorMessage } from '../api/client';
import { ChannelsInput, emailRowsRule, toEmailsPayload, toPhonesPayload } from './ContactChannels';

interface Created {
  CONTACT: ContactListItem;
  ORGANIZATION: OrganizationListItem;
  DEAL: DealItem;
}

const NOUN: Record<ObjectType, string> = { CONTACT: 'contact', ORGANIZATION: 'organization', DEAL: 'deal' };

/** Contact email and phone are lists (several, each with a label), edited like on the full contact form. */
const CHANNELS: Record<string, { name: 'emails' | 'phones'; kind: 'email' | 'phone' }> = { email: { name: 'emails', kind: 'email' }, phone: { name: 'phones', kind: 'phone' } };

interface Props<K extends ObjectType> {
  objectType: K;
  open: boolean;
  onClose: () => void;
  /** Called with the new record (the picker that opened the modal selects it). */
  onCreated?: (record: Created[K]) => void;
  /** Starting values by API property (e.g. `organizationId`, `contactId`, `name`); sent even when the field is not on the modal. */
  preset?: Record<string, unknown>;
}

/**
 * Small create form holding only the fields picked for quick add in Settings → Fields (plus every required one).
 * Opened from the + button next to contact, organization and deal pickers.
 */
export function QuickCreateModal<K extends ObjectType>({ objectType, open, onClose, onCreated, preset }: Props<K>) {
  return (
    <Modal title={`New ${NOUN[objectType]}`} open={open} onCancel={onClose} footer={null} destroyOnHidden width={560}>
      {/* the modal is portalled but React events still bubble to the form around the picker; keep submits in here */}
      <div onSubmit={(e) => e.stopPropagation()}>
        <QuickCreateForm objectType={objectType} onClose={onClose} onCreated={onCreated} preset={preset} />
      </div>
    </Modal>
  );
}

function QuickCreateForm<K extends ObjectType>({ objectType, onClose, onCreated, preset }: Omit<Props<K>, 'open'>) {
  const { message } = App.useApp();
  const defs = useFields(objectType);
  const fields = useMemo(() => quickAddFields(defs.data ?? []), [defs.data]);
  const isChannel = (key: string) => objectType === 'CONTACT' && !!CHANNELS[key];
  const [form] = Form.useForm();
  const createContact = useCreateContact();
  const createOrganization = useCreateOrganization();
  const createDeal = useCreateDeal();
  const saving = createContact.isPending || createOrganization.isPending || createDeal.isPending;

  // deals need a pipeline and stage: the default pipeline's first open stage unless picked
  const isDeal = objectType === 'DEAL';
  const pipelines = usePipelines(isDeal);
  const [pipelineId, setPipelineId] = useState(preset?.pipelineId as string | undefined);
  const [stageId, setStageId] = useState(preset?.stageId as string | undefined);
  useEffect(() => {
    if (isDeal && !pipelineId && pipelines.data?.length) setPipelineId(pipelines.data.find((p) => p.isDefault)?.id ?? pipelines.data[0].id);
  }, [isDeal, pipelineId, pipelines.data]);
  const stages = pipelines.data?.find((p) => p.id === pipelineId)?.stages ?? [];
  const effectiveStage = stages.some((s) => s.id === stageId) ? stageId : stages.find((s) => s.kind === 'OPEN')?.id;

  const submit = async (values: Record<string, unknown>) => {
    const payload = { ...preset, ...fromFormValues(fields.filter((d) => !isChannel(d.key)), values) };
    if (objectType === 'CONTACT') {
      if (fields.some((d) => d.key === 'email')) payload.emails = toEmailsPayload(values.emails as never);
      if (fields.some((d) => d.key === 'phone')) payload.phones = toPhonesPayload(values.phones as never);
    }
    payload.fields = { ...((preset?.fields as object) ?? {}), ...(payload.fields as object) };
    try {
      let saved: Created[ObjectType];
      if (objectType === 'CONTACT') saved = await createContact.mutateAsync(payload as Parameters<typeof createContact.mutateAsync>[0]);
      else if (objectType === 'ORGANIZATION') saved = await createOrganization.mutateAsync(payload as Parameters<typeof createOrganization.mutateAsync>[0]);
      else saved = await createDeal.mutateAsync({ ...payload, pipelineId, stageId: effectiveStage } as Parameters<typeof createDeal.mutateAsync>[0]);
      message.success(`${NOUN[objectType][0].toUpperCase()}${NOUN[objectType].slice(1)} created`);
      onCreated?.(saved as Created[K]);
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  if (defs.isError) return <Alert type="error" showIcon title="Could not load the fields. Close and try again." />;
  if (!defs.data || (isDeal && pipelines.isLoading)) return <Skeleton active />;
  const canSave = !isDeal || !!effectiveStage;

  return (
    <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} initialValues={{ fields: {}, emails: [], phones: [], subscribed: true, ...preset }}>
      {isDeal && (
        <>
          {pipelineId && !effectiveStage && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="This pipeline has no open stages. Add one under Settings → Pipelines." />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
            <Form.Item label="Pipeline">
              <Select value={pipelineId} onChange={(v) => { setPipelineId(v); setStageId(undefined); }} options={(pipelines.data ?? []).map((p) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
            <Form.Item label="Stage">
              <Select value={effectiveStage} onChange={setStageId} options={stages.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          </div>
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
        {fields.map((d) => (
          // pickers get the full width so their + button has room
          <div key={d.key} style={{ gridColumn: d.type === 'TEXTAREA' || d.key.endsWith('_id') || isChannel(d.key) ? '1 / -1' : undefined }}>
            {isChannel(d.key) ? (
              <Form.Item name={CHANNELS[d.key].name} label={d.key === 'email' ? 'Emails' : 'Phones'} rules={d.key === 'email' ? [emailRowsRule] : undefined}>
                <ChannelsInput kind={CHANNELS[d.key].kind} />
              </Form.Item>
            ) : (
              <FieldFormItem def={d} />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ marginInlineEnd: 'auto', fontSize: 12 }}>
          Pick these fields in <Link to="/settings/fields" onClick={onClose}>Settings → Fields</Link>
        </Typography.Text>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" htmlType="submit" loading={saving} disabled={!canSave}>
          Create
        </Button>
      </div>
    </Form>
  );
}
